<template>
    <div class="contact-module-rules">
        <div class="page-header">
            <div class="header-left">
                <h2>{{ $t("contactModuleRules.title") }}</h2>
                <p class="description">
                    {{ $t("contactModuleRules.description") }}
                </p>
            </div>
            <div class="header-right">
                <a-switch v-model="enabled" @change="onMasterToggle" size="large" />
                <span class="toggle-label">{{ enabled ? $t("contactModuleRules.active") : $t("contactModuleRules.passive") }}</span>
            </div>
        </div>

        <div v-if="loading" class="loading-container">
            <a-spin size="large" />
        </div>

        <div v-else-if="enabled" class="rules-container">
            <!-- Rules List -->
            <draggable v-model="rules" handle=".drag-handle" @end="onRulesReordered" class="rules-list">
                <div v-for="rule in rules" :key="rule.id" class="rule-card" :class="{ disabled: !rule.enabled }">
                    <div class="rule-header">
                        <div class="rule-header-left">
                            <i class="material-icons drag-handle">drag_indicator</i>
                            <span class="rule-name">{{ rule.name }}</span>
                        </div>
                        <div class="rule-header-right">
                            <a-switch v-model="rule.enabled" size="small" @change="onRuleToggle(rule.id, $event)" />
                            <a-button type="text" size="small" @click="editRule(rule)">
                                <i class="material-icons">edit</i>
                            </a-button>
                            <a-button type="text" size="small" danger @click="confirmDeleteRule(rule)">
                                <i class="material-icons">delete</i>
                            </a-button>
                        </div>
                    </div>

                    <div class="rule-body">
                        <!-- Semantic Summary -->
                        <div class="rule-summary">
                            <div class="summary-line when-summary">
                                <i class="material-icons">schedule</i>
                                <strong>{{ $t("contactModuleRules.when") }}</strong>
                                <span>{{ getWhenLabel(rule.when) }}</span>
                            </div>

                            <div class="summary-line conditions-summary">
                                <i class="material-icons">filter_list</i>
                                <strong>{{ $t("contactModuleRules.conditions") }}</strong>
                                <span v-html="getConditionsSummary(rule.conditions)"></span>
                            </div>

                            <div class="summary-line actions-summary">
                                <i class="material-icons">bolt</i>
                                <strong>{{ $t("contactModuleRules.actions") }}</strong>
                                <span v-html="getActionsSummary(rule.actions)"></span>
                            </div>
                        </div>
                    </div>
                </div>
            </draggable>

            <!-- Else Fallback Card -->
            <div class="else-card">
                <div class="else-header">
                    <div class="else-title">
                        <i class="material-icons">block</i>
                        <span>{{ $t("contactModuleRules.elseTitle") }}</span>
                    </div>
                    <a-button type="text" size="small" @click="editElseActions">
                        <i class="material-icons">edit</i>
                    </a-button>
                </div>
                <div class="else-body">
                    <div v-if="elseActions.length === 0" class="no-actions">{{ $t("contactModuleRules.noActionsDefined") }}</div>
                    <div v-else class="else-summary">
                        <div class="summary-line actions-summary">
                            <i class="material-icons">bolt</i>
                            <strong>{{ $t("contactModuleRules.actions") }}</strong>
                            <span v-html="getActionsSummary(elseActions)"></span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Add Rule Button -->
            <div class="add-rule-btn" @click="createNewRule">
                <i class="material-icons">add_circle</i>
                <span>{{ $t("contactModuleRules.addNewRule") }}</span>
            </div>
        </div>

        <div v-else class="empty-state">
            <div class="empty-icon">
                <i class="material-icons">account_tree</i>
            </div>
            <h3>{{ $t("contactModuleRules.rulesDisabled") }}</h3>
            <p>{{ $t("contactModuleRules.enableHint") }}</p>
        </div>

        <!-- Rule Editor Modal -->
        <RuleEditor
            v-if="showRuleEditor"
            :rule="editingRule"
            :visible="showRuleEditor"
            @close="closeRuleEditor"
            @save="saveRule"
        />

        <!-- Else Actions Editor Modal -->
        <ElseActionsEditor
            v-if="showElseActionsEditor"
            :actions="elseActions"
            :visible="showElseActionsEditor"
            @close="closeElseActionsEditor"
            @save="saveElseActions"
        />

        <!-- Rule Tester Modal -->
        <RuleTester v-if="showRuleTester" :visible="showRuleTester" @close="showRuleTester = false" />
    </div>
</template>

<script>
import { mapState } from "vuex";
import draggable from "vuedraggable";
import RuleEditor from "./RuleEditor.vue";
import ElseActionsEditor from "./components/ElseActionsEditor.vue";
import RuleTester from "./RuleTester.vue";
import log2 from "../../../../../utils/log2";
import _ from "lodash";

const log = log2("ContactModuleRules");

export default {
    name: "ContactModuleRules",
    components: {
        draggable,
        RuleEditor,
        ElseActionsEditor,
        RuleTester,
    },
    props: {
        updateSettings: {
            type: Function,
            required: false,
        },
    },
    data() {
        return {
            showRuleEditor: false,
            showElseActionsEditor: false,
            showRuleTester: false,
            editingRule: null,
            loading: false,
        };
    },
    computed: {
        ...mapState({
            settings: (state) => state.setting.settings,
            initialized: (state) => state.setting.initialized,
        }),
        contactModuleRules() {
            return this.settings.contactModuleRules || { enabled: false, rules: [], elseActions: [] };
        },
        rules: {
            get() {
                return this.contactModuleRules.rules || [];
            },
            set(value) {
                const newSettings = _.cloneDeep(this.settings);
                if (!newSettings.contactModuleRules) {
                    newSettings.contactModuleRules = { enabled: false, rules: [], elseActions: [] };
                }
                newSettings.contactModuleRules.rules = value;
                this.$store.commit("setting/SET_SETTINGS", newSettings);
            },
        },
        elseActions: {
            get() {
                return this.contactModuleRules.elseActions || [];
            },
            set(value) {
                const newSettings = _.cloneDeep(this.settings);
                if (!newSettings.contactModuleRules) {
                    newSettings.contactModuleRules = { enabled: false, rules: [], elseActions: [] };
                }
                newSettings.contactModuleRules.elseActions = value;
                this.$store.commit("setting/SET_SETTINGS", newSettings);
            },
        },
        enabled: {
            get() {
                return this.contactModuleRules.enabled || false;
            },
            set(value) {
                const newSettings = _.cloneDeep(this.settings);
                if (!newSettings.contactModuleRules) {
                    newSettings.contactModuleRules = { enabled: false, rules: [], elseActions: [] };
                }
                newSettings.contactModuleRules.enabled = value;
                this.$store.commit("setting/SET_SETTINGS", newSettings);
            },
        },
    },
    methods: {
        async saveToBackend() {
            this.loading = true;
            try {
                await this.$store.dispatch("setting/updateSettings", this.settings);
                return true;
            } catch (error) {
                log("saveToBackend error", error);
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async onMasterToggle(enabled) {
            this.enabled = enabled;
            await this.saveToBackend();
            this.$message.success(
                enabled ? this.$t("contactModuleRules.rulesEnabledSuccess") : this.$t("contactModuleRules.rulesDisabledSuccess")
            );
        },

        async onRuleToggle(ruleId, enabled) {
            try {
                const rule = this.rules.find((r) => r.id === ruleId);
                if (rule) {
                    rule.enabled = enabled;
                    rule.updatedAt = new Date().toISOString();
                    await this.saveToBackend();
                    this.$message.success(enabled ? this.$t("contactModuleRules.ruleEnabledSuccess") : this.$t("contactModuleRules.ruleDisabledSuccess"));
                }
            } catch (error) {
                log("onRuleToggle error", error);
                this.$message.error(this.$t("contactModuleRules.ruleUpdateError"));
            }
        },

        async onRulesReordered() {
            const ruleIds = this.rules.map((r) => r.id);
            try {
                ruleIds.forEach((ruleId, index) => {
                    const rule = this.rules.find((r) => r.id === ruleId);
                    if (rule) {
                        rule.order = index;
                    }
                });
                await this.saveToBackend();
                this.$message.success(this.$t("contactModuleRules.orderUpdatedSuccess"));
            } catch (error) {
                log("onRulesReordered error", error);
                this.$message.error(this.$t("contactModuleRules.orderUpdateError"));
            }
        },

        createNewRule() {
            this.editingRule = {
                id: this.generateUUID(),
                name: this.$t("contactModuleRules.ruleNameTemplate", { n: this.rules.length + 1 }),
                enabled: true,
                order: this.rules.length,
                when: "contact_created",
                conditions: {
                    filterGroups: [],
                    groupOperator: "AND",
                },
                actions: [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            this.showRuleEditor = true;
        },

        editRule(rule) {
            const ruleCopy = JSON.parse(JSON.stringify(rule));
            // Date'leri string olarak sakla
            if (ruleCopy.createdAt && typeof ruleCopy.createdAt !== "string") {
                ruleCopy.createdAt = new Date(ruleCopy.createdAt).toISOString();
            }
            if (ruleCopy.updatedAt && typeof ruleCopy.updatedAt !== "string") {
                ruleCopy.updatedAt = new Date(ruleCopy.updatedAt).toISOString();
            }
            this.editingRule = ruleCopy;
            this.showRuleEditor = true;
        },

        closeRuleEditor() {
            this.showRuleEditor = false;
            this.editingRule = null;
        },

        async saveRule(rule) {
            try {
                // Date'leri string formatına çevir
                const ruleCopy = JSON.parse(JSON.stringify(rule));
                if (ruleCopy.createdAt && typeof ruleCopy.createdAt !== "string") {
                    ruleCopy.createdAt = new Date(ruleCopy.createdAt).toISOString();
                }
                if (ruleCopy.updatedAt && typeof ruleCopy.updatedAt !== "string") {
                    ruleCopy.updatedAt = new Date(ruleCopy.updatedAt).toISOString();
                }

                const existingIndex = this.rules.findIndex((r) => r.id === ruleCopy.id);
                const rulesCopy = [...this.rules];
                if (existingIndex !== -1) {
                    rulesCopy[existingIndex] = ruleCopy;
                } else {
                    rulesCopy.push(ruleCopy);
                }
                this.rules = rulesCopy;
                await this.saveToBackend();
                this.$message.success(this.$t("contactModuleRules.ruleSavedSuccess"));
                this.closeRuleEditor();
            } catch (error) {
                log("saveRule error", error);
                this.$message.error(this.$t("contactModuleRules.ruleSaveError"));
            }
        },

        confirmDeleteRule(rule) {
            this.$confirm({
                title: this.$t("contactModuleRules.deleteRuleTitle"),
                content: this.$t("contactModuleRules.deleteRuleConfirm", { name: rule.name }),
                okText: this.$t("contactModuleRules.delete"),
                okType: "danger",
                cancelText: this.$t("message.cancel"),
                onOk: async () => {
                    try {
                        const filteredRules = this.rules.filter((r) => r.id !== rule.id);
                        this.rules = filteredRules;
                        await this.saveToBackend();
                        this.$message.success(this.$t("contactModuleRules.ruleDeletedSuccess"));
                    } catch (error) {
                        log("confirmDeleteRule error", error);
                        this.$message.error(this.$t("contactModuleRules.ruleDeleteError"));
                    }
                },
            });
        },

        editElseActions() {
            this.showElseActionsEditor = true;
        },

        closeElseActionsEditor() {
            this.showElseActionsEditor = false;
        },

        async saveElseActions(actions) {
            try {
                this.elseActions = actions;
                await this.saveToBackend();
                this.$message.success(this.$t("contactModuleRules.elseActionsSavedSuccess"));
                this.closeElseActionsEditor();
            } catch (error) {
                log("saveElseActions error", error);
                this.$message.error(this.$t("contactModuleRules.actionsSaveError"));
            }
        },

        getWhenLabel(when) {
            return when === "contact_created" ? this.$t("contactModuleRules.whenContactCreated") : this.$t("contactModuleRules.whenChatOpened");
        },

        getWhenBadgeClass(when) {
            return when === "contact_created" ? "when-contact-created" : "when-chat-opened";
        },

        getConditionsSummary(conditions) {
            if (!conditions || !conditions.filterGroups || conditions.filterGroups.length === 0) {
                return '<span class="no-condition">' + this.$t("contactModuleRules.noCondition") + "</span>";
            }

            var andLabel = this.$t("contactModuleRules.and");
            var orLabel = this.$t("contactModuleRules.or");
            var ANDLabel = this.$t("contactModuleRules.AND");
            var ORLabel = this.$t("contactModuleRules.OR");

            const groupSummaries = conditions.filterGroups.map((group) => {
                const filterSummaries = group.filters
                    .map((filter) => {
                        return this.getFilterSummary(filter);
                    })
                    .join(" <strong>" + (group.operator === "AND" ? andLabel : orLabel) + "</strong> ");

                return "(" + filterSummaries + ")";
            });

            return groupSummaries.join(
                " <strong class=\"group-op\">" + (conditions.groupOperator === "AND" ? ANDLabel : ORLabel) + "</strong> "
            );
        },

        getFilterSummary(filter) {
            const fieldLabel = this.getFieldLabel(filter.field);
            const operatorLabel = this.getOperatorLabel(filter.operator);
            const valueLabel = this.getValueLabel(filter.field, filter.value);

            if (!filter.field) return '<span class="incomplete">' + this.$t("contactModuleRules.fieldNotSelected") + "</span>";
            if (!filter.operator) return `<span class="field-name">${fieldLabel}</span>`;
            if (!this.needsValue(filter.operator))
                return `<span class="field-name">${fieldLabel}</span> ${operatorLabel}`;

            return `<span class="field-name">${fieldLabel}</span> ${operatorLabel} <span class="value-highlight">${valueLabel}</span>`;
        },

        getFieldLabel(field) {
            var t = this.$t.bind(this);
            var labels = {
                "contact.fullname": t("contactModuleRules.fields.fullname"),
                "contact.email": t("contactModuleRules.fields.email"),
                "contact.phone": t("contactModuleRules.fields.phone"),
                "contact.whatsappNumber": t("contactModuleRules.fields.whatsappNumber"),
                "contact.whatsappCountry": t("contactModuleRules.fields.whatsappCountry"),
                "contact.platform": t("contactModuleRules.fields.platform"),
                "contact.owner.exists": t("contactModuleRules.fields.ownerExists"),
                "contact.owner.isOnline": t("contactModuleRules.fields.ownerIsOnline"),
                "contact.owner.status": t("contactModuleRules.fields.ownerStatus"),
                "contact.uptime": t("contactModuleRules.fields.uptime"),
                "contact.tags": t("contactModuleRules.fields.tags"),
                "contact.channel": t("contactModuleRules.fields.channel"),
                "contact.preferredDepartment": t("contactModuleRules.fields.preferredDepartment"),
                "system.timezone.hour": t("contactModuleRules.fields.hour"),
                "system.timezone.day": t("contactModuleRules.fields.day"),
                "conversation.status": t("contactModuleRules.fields.conversationStatus"),
                "conversation.subject": t("contactModuleRules.fields.conversationSubject"),
            };
            return labels[field] || field;
        },

        getOperatorLabel(operator) {
            var t = this.$t.bind(this);
            var labels = {
                equals: t("contactModuleRules.operators.equals"),
                not_equals: t("contactModuleRules.operators.not_equals"),
                contains: t("contactModuleRules.operators.contains"),
                not_contains: t("contactModuleRules.operators.not_contains"),
                gt: t("contactModuleRules.operators.gt"),
                gte: t("contactModuleRules.operators.gte"),
                lt: t("contactModuleRules.operators.lt"),
                lte: t("contactModuleRules.operators.lte"),
                in: t("contactModuleRules.operators.in"),
                not_in: t("contactModuleRules.operators.not_in"),
                exists: t("contactModuleRules.operators.exists"),
                not_exists: t("contactModuleRules.operators.not_exists"),
            };
            return labels[operator] || operator;
        },

        getValueLabel(field, value) {
            if (value === null || value === undefined || value === "") return "-";

            if (field === "contact.tags" && Array.isArray(value)) {
                const tagNames = value.map((tagId) => {
                    const tag = this.$store.state.tag.tags.data.find((t) => t._id === tagId);
                    return tag ? tag.name : tagId;
                });
                return tagNames.join(", ");
            }

            if (field === "contact.channel") {
                const channel = this.$store.state.channel.channels.find((ch) => ch._id === value);
                return channel ? channel.name : value;
            }

            if ((field === "contact.preferredDepartment" || field === "conversation.subject") && value) {
                const dept = this.$store.state.subject.subjects.data.find((d) => d._id === value);
                return dept ? dept.languageContent[this.$i18n.locale].name : value;
            }

            if (field === "contact.platform") {
                const platformLabels = {
                    whatsapp: "WhatsApp",
                    instagram: "Instagram",
                    telegram: "Telegram",
                    messenger: "Messenger",
                    linkedin: "LinkedIn",
                    web: "Web",
                    mobile: "Mobile",
                    email: "Email",
                    trendyol: "Trendyol",
                };
                return platformLabels[value] || value;
            }

            if (typeof value === "boolean") {
                return value ? this.$t("contactModuleRules.yes") : this.$t("contactModuleRules.no");
            }

            return value.toString();
        },

        getActionsSummary(actions) {
            var noActionLabel = this.$t("contactModuleRules.noAction");
            if (!actions || actions.length === 0) {
                return '<span class="no-action">' + noActionLabel + "</span>";
            }

            const actionSummaries = actions
                .map((action) => {
                    return this.getActionSummary(action);
                })
                .filter((s) => s)
                .join(", ");

            return actionSummaries || '<span class="no-action">' + noActionLabel + "</span>";
        },

        getActionSummary(action) {
            if (action.type === "none") {
                return '<span class="action-none">' + this.$t("contactModuleRules.doNothing") + "</span>";
            }

            if (action.type === "change-owner") {
                const config = action.changeOwner;
                if (config.method === "static") {
                    if (config.staticUserId === null) {
                        return '<span class="action-owner">' + this.$t("contactModuleRules.assignOwnerlessAction", { ownerless: this.$t("contactModuleRules.assignToOwnerless") }) + "</span>";
                    }
                    const user = this.$store.state.user.users.data.find((u) => u._id === config.staticUserId);
                    const userName = user ? user.name : this.$t("contactModuleRules.unknown");
                    return '<span class="action-owner">' + this.$t("contactModuleRules.assignToUser", { name: userName }) +
                        (config.syncToZoho ? " <em>(Zoho)</em>" : "") +
                    "</span>";
                }
                if (config.method === "algorithmic") {
                    const algo = config.algorithmSettings.algorithm === "least_contact" ? this.$t("contactModuleRules.leastContact") : this.$t("contactModuleRules.roundRobin");
                    const dateRange =
                        config.algorithmSettings.dateRange === "today"
                            ? this.$t("contactModuleRules.today")
                            : config.algorithmSettings.dateRange === "last_7_days"
                            ? this.$t("contactModuleRules.last7Days")
                            : this.$t("contactModuleRules.last30Days");
                    const users = config.useVisibilityControl
                        ? this.$t("contactModuleRules.channelAccessUsers")
                        : config.userList && config.userList.length > 0
                        ? this.$t("contactModuleRules.usersCount", { n: config.userList.length })
                        : this.$t("contactModuleRules.allUsers");
                    return '<span class="action-owner"><strong>' + this.$t("contactModuleRules.algorithmic") + "</strong> (" + algo + ", " + dateRange + ", " + users + ")" +
                        (config.syncToZoho ? " <em>(Zoho)</em>" : "") +
                    "</span>";
                }
                if (config.method === "random") {
                    const users = config.useVisibilityControl
                        ? this.$t("contactModuleRules.channelAccessUsers")
                        : config.userList && config.userList.length > 0
                        ? this.$t("contactModuleRules.usersCount", { n: config.userList.length })
                        : this.$t("contactModuleRules.allUsers");
                    return '<span class="action-owner"><strong>' + this.$t("contactModuleRules.random") + "</strong> (" + users + ")" +
                        (config.syncToZoho ? " <em>(Zoho)</em>" : "") +
                    "</span>";
                }
            }

            if (action.type === "add-tag") {
                const tagIds = action.addTag.tagIds || [];
                if (tagIds.length === 0) return '<span class="action-tag">' + this.$t("contactModuleRules.addTagNotSelected") + "</span>";
                const tagNames = tagIds
                    .map((tagId) => {
                        const tag = this.$store.state.tag.tags.data.find((t) => t._id === tagId);
                        return tag ? tag.name : tagId;
                    })
                    .join(", ");
                return '<span class="action-tag"><strong>' + tagNames + "</strong> " +
                    (tagIds.length > 1 ? this.$t("contactModuleRules.tagsLabel") : this.$t("contactModuleRules.tagLabel")) +
                    "</span>";
            }

            if (action.type === "add-department") {
                const deptId = action.addDepartment.departmentId;
                if (!deptId) return '<span class="action-dept">' + this.$t("contactModuleRules.addDepartmentNotSelected") + "</span>";
                const dept = this.$store.state.subject.subjects.data.find((d) => d._id === deptId);
                const deptName = dept ? dept.languageContent[this.$i18n.locale].name : deptId;
                const applyToConv = action.addDepartment.applyToConversation ? " <em>" + this.$t("contactModuleRules.applyToConversation") + "</em>" : "";
                return '<span class="action-dept"><strong>' + deptName + "</strong> " + this.$t("contactModuleRules.addDepartmentTo") + applyToConv + "</span>";
            }

            if (action.type === "upsert-contact-property") {
                const propId = action.upsertContactProperty.propertyId;
                const propValue = action.upsertContactProperty.value;
                if (!propId) return '<span class="action-prop">' + this.$t("contactModuleRules.updatePropertyNotSelected") + "</span>";
                const prop = this.$store.state.contactProperty.data.find((p) => p._id === propId);
                const propLabel = prop ? prop.label : propId;
                return `<span class="action-prop"><strong>${propLabel}</strong> = "${propValue}"</span>`;
            }

            return this.getActionLabel(action.type);
        },

        needsValue(operator) {
            return !["exists", "not_exists"].includes(operator);
        },

        getActionIcon(type) {
            const icons = {
                none: "block",
                "change-owner": "assignment_ind",
                "add-tag": "label",
                "add-department": "business",
                "upsert-contact-property": "edit_note",
            };
            return icons[type] || "help_outline";
        },

        getActionLabel(type) {
            var t = this.$t.bind(this);
            var labels = {
                none: t("contactModuleRules.doNothing"),
                "change-owner": t("contactModuleRules.changeOwner"),
                "add-tag": t("contactModuleRules.addTag"),
                "add-department": t("contactModuleRules.addDepartment"),
                "upsert-contact-property": t("contactModuleRules.updateProperty"),
            };
            return labels[type] || type;
        },

        getActionBadgeClass(type) {
            return `action-${type}`;
        },

        getActionTooltip(action) {
            return this.getActionLabel(action.type);
        },

        generateUUID() {
            return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
                const r = (Math.random() * 16) | 0;
                const v = c === "x" ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.contact-module-rules {
    padding: 24px;
    max-width: 1400px;
    

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 32px;
        padding-bottom: 24px;
        border-bottom: 2px solid #f0f0f0;

        .header-left {
            h2 {
                font-size: 24px;
                font-weight: 600;
                color: #1a1a1a;
                margin: 0 0 8px 0;
            }

            .description {
                color: #666;
                font-size: 14px;
                margin: 0;
            }
        }

        .header-right {
            display: flex;
            align-items: center;
            gap: 12px;

            .test-btn {
                height: 36px;
            }

            .toggle-label {
                font-weight: 600;
                font-size: 16px;
            }
        }
    }

    .loading-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 400px;
    }

    .rules-container {
        .rules-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-bottom: 16px;
        }

        .rule-card {
            background: #ffffff;
            border: 2px solid #e8e8e8;
            border-radius: 12px;
            padding: 20px;
            transition: all 0.3s ease;

            &:hover {
                border-color: #1890ff;
                box-shadow: 0 4px 12px rgba(24, 144, 255, 0.1);
            }

            &.disabled {
                opacity: 0.6;
                background: #fafafa;
            }

            .rule-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 16px;

                .rule-header-left {
                    display: flex;
                    align-items: center;
                    gap: 12px;

                    .drag-handle {
                        cursor: grab;
                        color: #999;
                        font-size: 20px;

                        &:active {
                            cursor: grabbing;
                        }
                    }

                    .rule-name {
                        font-size: 16px;
                        font-weight: 600;
                        color: #1a1a1a;
                    }
                }

                .rule-header-right {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .material-icons {
                        font-size: 18px;
                    }
                }
            }

            .rule-body {
                .rule-summary {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;

                    .summary-line {
                        display: flex;
                        align-items: flex-start;
                        gap: 8px;
                        font-size: 14px;
                        line-height: 1.6;

                        .material-icons {
                            font-size: 18px;
                            margin-top: 2px;
                        }

                        strong {
                            min-width: 80px;
                            color: #666;
                        }

                        span {
                            flex: 1;
                            color: #1a1a1a;

                            :deep(.field-name) {
                                color: #1890ff;
                                font-weight: 500;
                            }

                            :deep(.value-highlight) {
                                background: #fff7e6;
                                padding: 2px 6px;
                                border-radius: 3px;
                                color: #d46b08;
                                font-weight: 500;
                            }

                            :deep(.group-op) {
                                color: #9254de;
                                font-weight: 700;
                                padding: 0 4px;
                            }

                            :deep(.no-condition),
                            :deep(.no-action) {
                                color: #999;
                                font-style: italic;
                            }

                            :deep(.incomplete) {
                                color: #ff4d4f;
                                font-style: italic;
                            }

                            :deep(.action-owner) {
                                color: #1890ff;

                                strong {
                                    color: #096dd9;
                                }

                                em {
                                    color: #52c41a;
                                    font-size: 12px;
                                }
                            }

                            :deep(.action-tag) {
                                color: #ff9800;

                                strong {
                                    color: #d46b08;
                                }
                            }

                            :deep(.action-dept) {
                                color: #9c27b0;

                                strong {
                                    color: #722ed1;
                                }

                                em {
                                    color: #666;
                                    font-size: 12px;
                                }
                            }

                            :deep(.action-prop) {
                                color: #4caf50;

                                strong {
                                    color: #389e0d;
                                }
                            }

                            :deep(.action-none) {
                                color: #999;
                            }
                        }

                        &.when-summary {
                            .material-icons {
                                color: #1890ff;
                            }
                        }

                        &.conditions-summary {
                            .material-icons {
                                color: #52c41a;
                            }
                        }

                        &.actions-summary {
                            .material-icons {
                                color: #ff9800;
                            }
                        }
                    }
                }
            }
        }

        .else-card {
            background: #fafafa;
            border: 2px dashed #d9d9d9;
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 16px;

            .else-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 16px;

                .else-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 16px;
                    font-weight: 600;
                    color: #666;

                    .material-icons {
                        font-size: 20px;
                    }
                }
            }

            .else-body {
                .no-actions {
                    color: #999;
                    font-style: italic;
                    padding: 12px;
                    text-align: center;
                }

                .else-summary {
                    .summary-line {
                        display: flex;
                        align-items: flex-start;
                        gap: 8px;
                        font-size: 14px;
                        line-height: 1.6;

                        .material-icons {
                            font-size: 18px;
                            color: #ff9800;
                            margin-top: 2px;
                        }

                        strong {
                            min-width: 80px;
                            color: #666;
                        }

                        span {
                            flex: 1;
                            color: #1a1a1a;
                        }
                    }
                }
            }
        }

        .add-rule-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 20px;
            background: #ffffff;
            border: 2px dashed #1890ff;
            border-radius: 12px;
            cursor: pointer;
            color: #1890ff;
            font-weight: 600;
            transition: all 0.3s ease;

            &:hover {
                background: #e6f7ff;
                border-color: #40a9ff;
            }

            .material-icons {
                font-size: 24px;
            }
        }
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 400px;
        color: #999;

        .empty-icon {
            .material-icons {
                font-size: 80px;
                color: #d9d9d9;
            }
        }

        h3 {
            font-size: 20px;
            margin: 16px 0 8px 0;
            color: #666;
        }

        p {
            font-size: 14px;
            margin: 0;
        }
    }
}
</style>
